import { doc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { db, crashlytics } from "./firebase";

/**
 * Queue a user action to be processed by Cloud Function
 * Actions are stored in user document and processed every 2 hours
 */
export const queueUserAction = async (
  userId: string,
  type:
    | "create"
    | "join"
    | "decline"
    | "leave"
    | "delete"
    | "pass"
    | "update"
    | "kick"
    | "message"
    | "message_reaction"
    | "story_post"
    | "story_view"
    | "activity_view"
    | "search_query"
    | "notification_click"
    | "ai_chat_message"
    | "ai_idea_generate"
    | "follow_request"
    | "follow_accept"
    | "follow_decline"
    | "unfollow"
    | "remove_follower"
    | "cancel_follow_request"
    | "mutual_follow",
  activityId?: string,
  activityTitle?: string,
  category?: string,
  location?: string,
  metadata?: Record<string, any>,
) => {
  if (!userId) return;

  try {
    const userRef = doc(db, "users", userId);

    // Helper to remove undefined values recursively
    const cleanObject = (obj: any): any => {
      if (obj === null || obj === undefined) return undefined;
      if (typeof obj !== "object") return obj;
      if (Array.isArray(obj))
        return obj.map(cleanObject).filter((v) => v !== undefined);

      const cleaned: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = cleanObject(value);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    };

    // Build action object, filtering out undefined values (Firebase doesn't accept undefined)
    const action: Record<string, any> = {
      type,
      timestamp: Timestamp.now(),
    };

    // Only add fields that have values
    if (activityId !== undefined) action.activityId = activityId;
    if (activityTitle !== undefined) action.activityTitle = activityTitle;
    if (category !== undefined) action.category = category;
    if (location !== undefined) action.location = location;
    if (metadata !== undefined) {
      const cleanedMetadata = cleanObject(metadata);
      if (cleanedMetadata !== undefined) {
        action.metadata = cleanedMetadata;
      }
    }

    // Add action to pending queue (root level for Cloud Function)
    await updateDoc(userRef, {
      pendingActions: arrayUnion(action),
    });
  } catch (error) {
    crashlytics.logError(
      `Error queueing user action: ${JSON.stringify(error?.message)}`,
      "userActionService.ts",
      error,
    );
  }
};
